import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { OfferAPI } from '../../../api/payments/offer/api';
import { PlansAPI } from '../../../api/payments/plans/api';
import { ItemsAPI } from '../../../api/payments/items/api';
import { buildItemsQueryParams } from '../../Items/utils';
import { ITEM_TYPE_ADDON, getItemPrice } from '../_components/OfferItemRow';
import {
  dateInputValueToIso,
  toDateInputValue,
} from '../../../helpers/dateInput';

const SEARCH_DEBOUNCE_MS = 400;
const ITEMS_LIMIT = 100;

function extractItems(response) {
  const payload = response.data?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  return response.data?.content || [];
}

export function useItemSelectBootstrap() {
  const [searchText, setSearchText] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  // Busca no servidor por nome com debounce de 400ms.
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      const currentRequest = ++requestId.current;
      setLoading(true);
      try {
        const params = buildItemsQueryParams({
          page: 0,
          limit: ITEMS_LIMIT,
          filterText: searchText,
        });
        const response = await ItemsAPI.list(params);
        if (currentRequest === requestId.current) {
          setItems(extractItems(response));
        }
      } catch (error) {
        if (currentRequest === requestId.current) {
          setItems([]);
          toast.error(error?.message || 'Erro ao buscar itens.');
        }
      } finally {
        if (currentRequest === requestId.current) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [searchText]);

  return { items, loading, setSearchText };
}

const DEFAULT_PAYMENT_METHOD = 'CREDIT_CARD';

const PAYMENT_METHOD_LABELS = {
  CREDIT_CARD: 'Cartão de crédito',
  PIX: 'Pix',
  BOLETO: 'Boleto',
};

// Usado caso a listagem de métodos de pagamento não possa ser carregada.
const FALLBACK_PAYMENT_METHODS = Object.keys(PAYMENT_METHOD_LABELS);

function normalizePaymentMethods(response) {
  const list = Array.isArray(response) ? response : response?.data ?? [];
  return list
    .map((method) => (typeof method === 'string' ? method : method?.id))
    .filter(Boolean)
    .map((id) => ({ id, label: PAYMENT_METHOD_LABELS[id] ?? id }));
}

// O e-mail pode vir de outra página via query string (?email=) ou location.state.
function getRedirectEmail(location) {
  const fromQuery = new URLSearchParams(location.search).get('email');
  return fromQuery || location.state?.email || '';
}

// Estimativa client-side só do 1º ciclo de cobrança (o valor final real de
// cada ciclo é calculado pelo backend). Regras com planId que não bate com o
// plano selecionado são ignoradas; regras ADDON_TOTAL/ADDON_ITEM são aplicadas
// sobre os add-ons mesmo quando têm um addonId específico — o catálogo de
// "Addons" do cupom é outra entidade (AddonsAPI) e não dá pra cruzar com os
// "items" (ItemsAPI) usados aqui, então tratamos como desconto geral dos
// add-ons, não de um produto específico.
function ruleAppliesAtFirstCycle(rule) {
  const from = rule.fromCycle == null ? 1 : Number(rule.fromCycle);
  const to = rule.toCycle == null ? Infinity : Number(rule.toCycle);
  return from <= 1 && to >= 1;
}

function applyRule(amount, rule) {
  const value = Number(rule.value);
  if (!Number.isFinite(value)) return amount;
  const valueType = rule.valueType ?? rule.value_type;
  const discounted =
    valueType === 'PERCENT' ? amount * (1 - value / 100) : amount - value;
  return Math.max(0, discounted);
}

// Reduz proporcionalmente cada linha pra manter a soma das linhas igual ao
// valor da regra aplicada no agregado (exato pra PERCENT; alocação por
// participação pra FIXED, já que um desconto fixo sobre a soma não aponta
// pra uma linha específica).
function scaleRows(rowAmounts, rule) {
  const sumBefore = [...rowAmounts.values()].reduce((sum, v) => sum + v, 0);
  if (sumBefore <= 0) return;
  const sumAfter = applyRule(sumBefore, rule);
  const ratio = sumAfter / sumBefore;
  rowAmounts.forEach((amount, key) => rowAmounts.set(key, amount * ratio));
}

function buildOfferSummary(selectedPlan, items, coupon) {
  const planAmount = selectedPlan?.amount ?? 0;
  const rows = items
    .filter((item) => item.item)
    .map((item) => ({
      key: item.key,
      original: getItemPrice(item.item) * item.quantity,
    }));
  const itemsTotal = rows.reduce((sum, row) => sum + row.original, 0);
  const subtotal = planAmount + itemsTotal;

  const applicableRules = (coupon?.rules ?? []).filter(
    (rule) =>
      ruleAppliesAtFirstCycle(rule) &&
      (rule.scope !== 'PLAN' ||
        !rule.planId ||
        rule.planId === selectedPlan?.id),
  );

  let discountedPlan = planAmount;
  applicableRules
    .filter((rule) => rule.scope === 'PLAN')
    .forEach((rule) => {
      discountedPlan = applyRule(discountedPlan, rule);
    });

  // "A cada produto" (ADDON_ITEM) desconta linha a linha, exato; "Total em
  // produtos" (ADDON_TOTAL) desconta a soma, então rateamos entre as linhas.
  const rowAmounts = new Map(rows.map((row) => [row.key, row.original]));
  applicableRules
    .filter((rule) => rule.scope === 'ADDON_ITEM')
    .forEach((rule) => {
      rowAmounts.forEach((amount, key) =>
        rowAmounts.set(key, applyRule(amount, rule)),
      );
    });
  applicableRules
    .filter((rule) => rule.scope === 'ADDON_TOTAL')
    .forEach((rule) => scaleRows(rowAmounts, rule));

  let discountedItems = [...rowAmounts.values()].reduce((sum, v) => sum + v, 0);

  let finalTotal = discountedPlan + discountedItems;
  applicableRules
    .filter((rule) => rule.scope === 'ALL')
    .forEach((rule) => {
      // Desconto "ALL" incide sobre plano + add-ons juntos; rateia nas linhas
      // de add-on na mesma proporção pra manter a exibição por item coerente
      // com o total final mostrado no resumo.
      const before = finalTotal;
      finalTotal = applyRule(finalTotal, rule);
      if (before > 0) {
        const ratio = finalTotal / before;
        rowAmounts.forEach((amount, key) =>
          rowAmounts.set(key, amount * ratio),
        );
      }
    });
  discountedItems = [...rowAmounts.values()].reduce((sum, v) => sum + v, 0);

  const itemDiscounts = {};
  rows.forEach((row) => {
    const discounted = rowAmounts.get(row.key);
    if (discounted < row.original - 0.005) {
      itemDiscounts[row.key] = { original: row.original, discounted };
    }
  });

  return {
    subtotal,
    finalTotal,
    discountAmount: Math.max(0, subtotal - finalTotal),
    itemDiscounts,
  };
}

function mapAddonToItem(addon) {
  return {
    key: `addon-${addon.itemId}`,
    type: ITEM_TYPE_ADDON,
    itemId: addon.itemId,
    item: { ...addon, id: addon.itemId, name: addon.itemName },
    quantity: addon.itemCount ?? 1,
  };
}

export function useCreateOfferBootstrap(
  offerId,
  initialOffer,
) {
  const history = useHistory();
  const location = useLocation();
  const redirectEmail = getRedirectEmail(location);

  const [offerFromState, setOfferFromState] = useState(
    initialOffer,
  );
  const [fetchingOffer, setFetchingOffer] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const nextItemKey = useRef(0);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true);

  useEffect(() => {
    PlansAPI.list()
      .then((response) => setPlans(response.data.content || []))
      .catch((error) => {
        setPlans([]);
        toast.error(error?.message || 'Erro ao carregar os planos.');
      })
      .finally(() => setPlansLoading(false));
    OfferAPI.listPaymentMethods()
      .then((response) => {
        const methods = normalizePaymentMethods(response);
        setPaymentMethods(
          methods.length
            ? methods
            : normalizePaymentMethods(FALLBACK_PAYMENT_METHODS),
        );
      })
      .catch(() =>
        setPaymentMethods(normalizePaymentMethods(FALLBACK_PAYMENT_METHODS)),
      )
      .finally(() => setPaymentMethodsLoading(false));
  }, []);

  useEffect(() => {
    if (!offerId || offerFromState) return;
    setFetchingOffer(true);
    OfferAPI.getV2(offerId)
      .then((response) => setOfferFromState(response.data ?? response))
      .catch(() => {
        toast.warn(
          'Não foi possível carregar os dados da oferta, tente novamente pela listagem.',
        );
        setNotFound(true);
      })
      .finally(() => setFetchingOffer(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  const customerEmail = offerFromState?.customerEmail ?? redirectEmail;

  const initialValues = {
    name: offerFromState?.name ?? '',
    planId: offerFromState?.planId ?? '',
    paymentMethods: offerFromState?.allowedPaymentMethods ?? [
      DEFAULT_PAYMENT_METHOD,
    ],
    expirationDate: toDateInputValue(offerFromState?.expirationDate),
    linkToCustomer: !!customerEmail,
    customerEmail,
    items: (offerFromState?.addons ?? []).map(mapAddonToItem),
    coupon: offerFromState?.coupon ?? null,
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: yup.object().shape({
      name: yup
        .string()
        .required('O campo nome da oferta é obrigatório!')
        .matches(
          /^[A-Z0-9_-]+$/,
          'Use apenas letras maiúsculas, números, "_" ou "-".',
        ),
      planId: yup.string().required('Selecione o plano base!'),
      paymentMethods: yup
        .array()
        .of(yup.string())
        .min(1, 'Selecione ao menos um método de pagamento!'),
      expirationDate: yup.string(),
      linkToCustomer: yup.boolean(),
      customerEmail: yup.string().when('linkToCustomer', {
        is: true,
        then: (schema) =>
          schema
            .email('Informe um e-mail válido')
            .required('O e-mail do cliente é obrigatório!'),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        const payload = {
          name: values.name,
          originFlow: 'url_subscription',
          planId: values.planId,
          allowedPaymentMethods: values.paymentMethods,
          expirationDate: dateInputValueToIso(values.expirationDate),
          customerEmail: values.linkToCustomer ? values.customerEmail : null,
          couponId: values.coupon ? values.coupon.id : null,
          addons: values.items
            .filter((item) => item.itemId)
            .map((item) => ({
              itemId: item.itemId,
              quantity: item.quantity,
            })),
        };
        if (offerId) {
          await OfferAPI.updateV2(offerId, payload);
        } else {
          await OfferAPI.createV2(payload);
        }
        toast.success('Oferta salva com sucesso!');
        history.push('/payments/offers');
      } catch (error) {
        toast.error(
          error?.message || 'Ocorreu um erro ao criar o link de pagamento.',
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleLinkToCustomerChange = (event) =>
    formik.setFieldValue('linkToCustomer', event.target.checked);

  const [extraPlan, setExtraPlan] = useState(null);
  useEffect(() => {
    const planId = formik.values.planId;
    if (!planId || plans.some((plan) => plan.id === planId)) {
      setExtraPlan(null);
      return;
    }
    PlansAPI.get(planId)
      .then((response) => setExtraPlan(response.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.planId, plans]);

  const selectedPlan =
    plans.find((plan) => plan.id === formik.values.planId) ??
    (extraPlan?.id === formik.values.planId ? extraPlan : null);
  const planItems = selectedPlan?.planItems ?? [];

  const offerSummary = buildOfferSummary(
    selectedPlan,
    formik.values.items,
    formik.values.coupon,
  );

  const handleAddItem = () =>
    formik.setFieldValue('items', [
      ...formik.values.items,
      {
        key: nextItemKey.current++,
        type: ITEM_TYPE_ADDON,
        itemId: '',
        item: null,
        quantity: 1,
      },
    ]);

  const handleItemChange = (key, changes) =>
    formik.setFieldValue(
      'items',
      formik.values.items.map((item) =>
        item.key === key ? { ...item, ...changes } : item,
      ),
    );

  const handleRemoveItem = (key) =>
    formik.setFieldValue(
      'items',
      formik.values.items.filter((item) => item.key !== key),
    );

  const handleCancel = () => history.push('/payments/offers');

  return {
    notFound,
    fetchingOffer,
    formik,
    plans,
    plansLoading,
    planItems,
    selectedPlan,
    offerSummary,
    handleAddItem,
    handleItemChange,
    handleRemoveItem,
    paymentMethods,
    paymentMethodsLoading,
    handleLinkToCustomerChange,
    handleCancel,
  };
}
